import React from 'react';
import * as Sentry from '@sentry/react';
import styled from '@emotion/styled';
import {
  MapPin,
  Clock,
  Smartphone,
  Facebook,
  Instagram,
  Copy,
} from 'lucide-react';
import { useContent } from 'context/ContentContext';
import { useSiteInfo } from 'hooks/useSiteInfo';
import { formatBusinessHours } from 'utils/businessHours';
import PageSection from 'components/ui/PageSection';
import ContactForm from 'components/forms/ContactForm';

const SectionHeading = styled.h2`
  ${({ theme }) => theme.mixins.textH2};
  text-align: center;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 16px;

  ${({ theme }) => theme.mediaQueries.md} {
    margin-bottom: 32px;
  }
`;

const Tagline = styled.p`
  ${({ theme }) => theme.mixins.textP1};
  text-align: center;
  color: ${({ theme }) => theme.colors.black};
  max-width: 918px;
  margin: 0 auto 32px;

  ${({ theme }) => theme.mediaQueries.md} {
    margin-bottom: 42px;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 20px;
  max-width: ${({ theme }) => theme.layouts.maxWidth};
  margin: 0 auto;

  ${({ theme }) => theme.mediaQueries.lg} {
    flex-direction: row;
    gap: 10px;
  }
`;

const ContactInfoCard = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.primary};
  border-radius: 8px;
  padding: 30px;
  color: ${({ theme }) => theme.colors.primaryLight};
  box-shadow: ${({ theme }) => theme.shadows.md};

  ${({ theme }) => theme.mediaQueries.lg} {
    flex: 1;
    padding: 40px;
  }
`;

const InfoBlocksWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  justify-content: flex-start;
`;

const InfoBlock = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
`;

const InfoBlockTitle = styled.span`
  ${({ theme }) => theme.mixins.textP1};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.white};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InfoText = styled.p`
  ${({ theme }) => theme.mixins.textP2};
  color: ${({ theme }) => theme.colors.primaryLight};
  margin-left: 32px;
`;

/* Phone/email links measured 16px tall on phones; give them a 44px row (#245). */
const ContactLink = styled.a`
  color: inherit;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  min-height: 4.4rem;
`;

/* The copy icons were bare SVGs with onClick — not focusable, no name. */
const CopyButton = styled.button`
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 4.4rem;
  min-height: 4.4rem;
  margin-left: 0.4rem;
  border-radius: 8px;

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadows.outline};
  }
`;

const StyledContactFormCard = styled.div`
  position: relative;
  width: 100%;
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: 8px;
  padding: 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: ${({ theme }) => theme.shadows.md};

  ${({ theme }) => theme.mediaQueries.lg} {
    flex: 1;
  }
`;

const SocialMediaSection = styled.div`
  margin-top: 32px;
  text-align: center;
`;

const SocialMediaTitle = styled.h4`
  ${({ theme }) => theme.mixins.textP2};
  text-align: center;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 10px;
`;

const SocialMediaIcons = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
`;

const IconLink = styled.a`
  color: ${({ theme }) => theme.colors.primary};
  transition: ${({ theme }) => theme.transitions.fast};
  display: flex;
  align-items: center;
  justify-content: center;
  /* 44px tap target around the 24px icon (#245) */
  min-width: 4.4rem;
  min-height: 4.4rem;

  ${({ theme }) => theme.mediaQueries.sm} {
    &:hover {
      color: ${({ theme }) => theme.colors.primaryDark};
    }
  }

  &:focus {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadows.outline};
    color: ${({ theme }) => theme.colors.primaryDark};
  }
`;

const Location = () => {
  const {
    location,
    hours: staticHours,
    social = {},
    messages = {},
  } = useContent();
  const { businessHours } = useSiteInfo();

  const handleCopy = (text) => {
    navigator.clipboard
      .writeText(text)
      .catch((err) => Sentry.captureException(err));
  };

  const { title, description, phone, email, addressOne } = location || {};

  // Convert Clover hours to display format with grouped days
  const convertCloverHoursToDisplay = (hours) => {
    const dayNames = [
      messages.days?.sunday || 'Sunday',
      messages.days?.monday || 'Monday',
      messages.days?.tuesday || 'Tuesday',
      messages.days?.wednesday || 'Wednesday',
      messages.days?.thursday || 'Thursday',
      messages.days?.friday || 'Friday',
      messages.days?.saturday || 'Saturday',
    ];
    const formatted = formatBusinessHours(hours);

    // Group consecutive days with the same hours
    const grouped = [];
    let currentGroup = null;

    for (let i = 0; i < dayNames.length; i++) {
      const time = formatted[i];

      if (!currentGroup || currentGroup.time !== time) {
        // Start a new group
        if (currentGroup) {
          grouped.push(currentGroup);
        }
        currentGroup = {
          startDay: dayNames[i],
          endDay: dayNames[i],
          startIndex: i,
          endIndex: i,
          time,
        };
      } else {
        // Extend current group
        currentGroup.endDay = dayNames[i];
        currentGroup.endIndex = i;
      }
    }

    // Add the last group
    if (currentGroup) {
      grouped.push(currentGroup);
    }

    // Format the grouped days
    return grouped.map((group) => ({
      day:
        group.startDay === group.endDay
          ? group.startDay
          : `${group.startDay} - ${group.endDay}`,
      time: group.time,
    }));
  };

  // Use live Clover hours if available, otherwise fall back to static
  const displayHours = businessHours
    ? convertCloverHoursToDisplay(businessHours)
    : staticHours || [];

  return (
    <PageSection
      id="location"
      aria-labelledby="location-heading"
      backgroundColor="tanLight"
    >
      <SectionHeading id="location-heading">{title}</SectionHeading>
      <Tagline>{description}</Tagline>

      <ContentWrapper>
        <ContactInfoCard>
          <InfoBlocksWrapper>
            <InfoBlock>
              <InfoTitleContainer>
                <InfoBlockTitle>
                  <MapPin size={24} aria-hidden="true" />
                  {messages.locationLabels?.restaurantLocation ||
                    'Restaurant Location'}
                </InfoBlockTitle>
                <InfoText>{addressOne}</InfoText>
              </InfoTitleContainer>
            </InfoBlock>
            <InfoBlock>
              <InfoTitleContainer>
                <InfoBlockTitle>
                  <Clock size={24} aria-hidden="true" />
                  {messages.locationLabels?.hours || 'Hours'}
                </InfoBlockTitle>
              </InfoTitleContainer>
              {displayHours.map((hour, index) => (
                <InfoText key={index}>
                  {hour.day}: {hour.time}
                </InfoText>
              ))}
            </InfoBlock>
            <InfoBlock>
              <InfoTitleContainer>
                <InfoBlockTitle>
                  <Smartphone size={24} aria-hidden="true" />
                  {messages.locationLabels?.contactInfo || 'Contact Info'}
                </InfoBlockTitle>
              </InfoTitleContainer>
              <InfoText>
                <ContactLink href={`tel:${phone}`}>{phone}</ContactLink>
                <CopyButton
                  type="button"
                  aria-label="Copy phone number"
                  onClick={() => handleCopy(phone)}
                >
                  <Copy size={16} aria-hidden="true" />
                </CopyButton>
              </InfoText>
              <InfoText>
                <ContactLink href={`mailto:${email}`}>{email}</ContactLink>
                <CopyButton
                  type="button"
                  aria-label="Copy email address"
                  onClick={() => handleCopy(email)}
                >
                  <Copy size={16} aria-hidden="true" />
                </CopyButton>
              </InfoText>
            </InfoBlock>
          </InfoBlocksWrapper>
        </ContactInfoCard>

        <StyledContactFormCard>
          <ContactForm />
        </StyledContactFormCard>
      </ContentWrapper>

      <SocialMediaSection>
        <SocialMediaTitle>
          {messages.locationLabels?.followUs || 'Follow Us On:'}
        </SocialMediaTitle>
        <SocialMediaIcons>
          <IconLink
            href={social.instagram}
            aria-label={messages.ariaLabels?.instagram || 'Instagram'}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Instagram size={24} />
          </IconLink>
          <IconLink
            href={social.facebook}
            aria-label={messages.ariaLabels?.facebook || 'Facebook'}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Facebook size={24} />
          </IconLink>
        </SocialMediaIcons>
      </SocialMediaSection>
    </PageSection>
  );
};

export default Location;
